// Visual card for a single indicator in the picker — matches bb-traderx's exact pattern:
// circular checkbox (●) + name + count badge + 2-line description + sparkline always inside.
// Card is `rounded-xl border-2`, primary-tinted when selected, hover-bright when not.

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getIndicatorShape, shapeToPath } from "@/lib/indicator-shapes";
import type { IndicatorCatalogEntry } from "../../api/indicators";

interface Props {
  indicator: IndicatorCatalogEntry;
  instanceCount: number;
  onAdd: () => void;
}

const SPARK_W = 120;
const SPARK_H = 36;

export function IndicatorCard({ indicator, instanceCount, onAdd }: Props) {
  const path = shapeToPath(getIndicatorShape(indicator.baseId), SPARK_W, SPARK_H, 2);
  const isSelected = instanceCount > 0;

  return (
    <Card
      onClick={onAdd}
      className={cn(
        "flex flex-col gap-1.5 p-2.5 rounded-xl border-2 cursor-pointer transition-all",
        isSelected
          ? "border-primary bg-primary/10"
          : "border-border bg-card-elevated hover:border-primary/50",
      )}
    >
      <div className="flex items-start gap-2 w-full">
        {/* Circular checkbox indicator */}
        <div
          className={cn(
            "mt-0.5 size-3.5 rounded-full border-2 shrink-0 flex items-center justify-center transition-all",
            isSelected ? "border-primary bg-primary" : "border-border",
          )}
        >
          {isSelected && <div className="size-1 rounded-full bg-white" />}
        </div>

        <div className="min-w-0 flex-1">
          <div
            className={cn(
              "text-xs font-semibold truncate flex items-center gap-1.5",
              isSelected ? "text-primary" : "text-foreground",
            )}
          >
            {indicator.displayName}
            {instanceCount > 1 && (
              <span className="text-[10px] font-bold px-1.5 py-px rounded-full bg-primary/30 text-primary">
                {instanceCount}×
              </span>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
            {indicator.description}
          </div>
        </div>
      </div>

      {/* Sparkline — always inside, opacity-based "selected" state matches bb-traderx */}
      <svg
        viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
        className="w-full"
        style={{ height: SPARK_H }}
        preserveAspectRatio="none"
      >
        <path
          d={path}
          fill="none"
          stroke={isSelected ? "var(--primary)" : "#6b7280"}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity={isSelected ? 0.9 : 0.45}
        />
      </svg>
    </Card>
  );
}
